import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGrip } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import PinPadModal from '../components/PinPadModal/PinPadModal';
import { fetchLoginUsers, type LoginUserOption } from '../auth/loginUsers';
import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';
import PasswordRecoveryModal from '../components/PasswordRecoveryModal/PasswordRecoveryModal';
import PasswordInput from '../components/PasswordInput/PasswordInput';
import storyOwlLoginLogo from '../assets/storyowl-login-logo-no-margins.png';

interface LoginPageProps {
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f5efe6 0%, #efe2d0 100%)',
  padding: '2rem',
};

const loginPalette = {
  primary: '#300000',
  secondary: '#DACBAE',
  text: '#DEDEDE',
  accent: '#460000',
  muted: '#9C6D5B',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: loginPalette.accent,
  border: `1px solid ${loginPalette.muted}`,
  borderRadius: 14,
  padding: '2rem',
  boxShadow: '0 16px 36px rgba(24, 0, 0, 0.34)',
};

const loginSelectOptionStyle: React.CSSProperties = {
  color: loginPalette.primary,
  background: '#f8f2e8',
};

const LOGIN_USER_STORAGE_KEY = 'tableArrangement_loginUser';

export default function LoginPage({ showToast }: LoginPageProps) {
  const { t } = useI18n();
  const { login, loginWithPin, backendReachable, auth, loading } = useAuth();
  const [email, setEmail] = useState(() => {
    if (typeof window === 'undefined') {
      return auth?.user?.email ?? '';
    }
    return window.localStorage.getItem(LOGIN_USER_STORAGE_KEY) ?? auth?.user?.email ?? '';
  });
  const [password, setPassword] = useState('');
  const [showPinPad, setShowPinPad] = useState(false);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<LoginUserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const selectedUser = availableUsers.find((user) => user.email === email);

  useEffect(() => {
    let cancelled = false;

    async function loadLoginUsers() {
      if (!backendReachable) {
        return;
      }

      setLoadingUsers(true);
      try {
        const users = await fetchLoginUsers();
        if (cancelled) {
          return;
        }
        setAvailableUsers(users);
        setEmail((current) => {
          const preferredEmail = current || auth?.user?.email || users[0]?.email || '';
          const matchedEmail = users.some((user) => user.email === preferredEmail) ? preferredEmail : users[0]?.email || '';
          if (typeof window !== 'undefined' && matchedEmail) {
            window.localStorage.setItem(LOGIN_USER_STORAGE_KEY, matchedEmail);
          }
          return matchedEmail;
        });
      } catch {
        if (!cancelled) {
          setAvailableUsers([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingUsers(false);
        }
      }
    }

    void loadLoginUsers();

    return () => {
      cancelled = true;
    };
  }, [backendReachable, auth?.user?.email]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await login(email, password);
    } catch (submitError) {
      if (submitError instanceof Error && submitError.message.toLowerCase().includes('invalid credentials')) {
        showToast(t('login.invalidCredentials'), 'error');
        return;
      }
      showToast(submitError instanceof Error ? submitError.message : t('login.error'), 'error');
    }
  }

  return (
    <div style={containerStyle}>
      <form style={cardStyle} onSubmit={handleSubmit}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <img
            src={storyOwlLoginLogo}
            alt="Story Owl - Livraria Cafe"
            style={{
              width: 148,
              height: 'auto',
              display: 'block',
            }}
          />
          <LanguageSwitcher className="login-language-switcher" minWidth={140} />
        </div>
        <h1 style={{ marginTop: 0, color: loginPalette.secondary }}>{t('app.title')}</h1>
        <p style={{ color: loginPalette.text }}>
          {backendReachable
            ? t('login.introOnline')
            : t('login.backendUnavailable')}
        </p>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', marginBottom: 6, color: loginPalette.secondary }}>{t('login.user')}</span>
          <select
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: 8,
              border: `1px solid ${loginPalette.muted}`,
              background: 'rgba(218, 203, 174, 0.08)',
              color: loginPalette.text,
            }}
            value={email}
            onChange={(event) => {
              const nextEmail = event.target.value;
              setEmail(nextEmail);
              if (typeof window !== 'undefined') {
                window.localStorage.setItem(LOGIN_USER_STORAGE_KEY, nextEmail);
              }
            }}
            disabled={loadingUsers || availableUsers.length === 0}
          >
            {availableUsers.length === 0 ? (
              <option value="" style={loginSelectOptionStyle}>
                {t('login.noUsersAvailable')}
              </option>
            ) : null}
            {availableUsers.map((user) => (
              <option key={user.email} value={user.email} style={loginSelectOptionStyle}>
                {user.displayName}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', marginBottom: 6, color: loginPalette.secondary }}>{t('login.password')}</span>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
            <PasswordInput
              value={password}
              onChange={setPassword}
              usernameValue={email}
              placeholder={t('login.password')}
              autoComplete="current-password"
              toggleButtonClassName="login-password-input-toggle"
              inputStyle={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: 8,
                border: `1px solid ${loginPalette.muted}`,
                background: 'rgba(218, 203, 174, 0.08)',
                color: loginPalette.text,
              }}
              className="login-password-field"
            />
            <button
              type="button"
              onClick={() => setShowPinPad(true)}
              title={t('login.usePin')}
              aria-label={t('login.usePin')}
              style={{
                flex: '0 0 44px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                border: `1px solid ${loginPalette.muted}`,
                background: 'rgba(218, 203, 174, 0.14)',
                color: loginPalette.secondary,
                cursor: 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faGrip} />
            </button>
          </div>
        </label>
        <button
          type="button"
          onClick={() => setShowPasswordRecovery(true)}
          style={{
            border: 'none',
            background: 'transparent',
            color: loginPalette.secondary,
            padding: 0,
            marginBottom: 14,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          {t('passwordReset.forgotPassword')}
        </button>
        {auth?.authExpired ? (
          <p style={{ color: '#f4d2c8' }}>
            {t('login.expired')}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.9rem',
            borderRadius: 10,
            border: 'none',
            background: loading ? loginPalette.muted : loginPalette.secondary,
            color: loading ? loginPalette.text : loginPalette.primary,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 700,
          }}
        >
          {loading ? t('login.signingIn') : t('login.signIn')}
        </button>
      </form>
      {showPinPad && (
        <PinPadModal
          email={email}
          displayName={selectedUser?.displayName}
          loading={loading}
          onClose={() => setShowPinPad(false)}
          onSubmit={(pin) => loginWithPin(email, pin)}
          showToast={showToast}
        />
      )}
      {showPasswordRecovery && (
        <PasswordRecoveryModal
          initialEmail={email}
          onClose={() => setShowPasswordRecovery(false)}
          showToast={showToast}
        />
      )}
    </div>
  );
}
